import React, { useState } from 'react';
import PropTypes from 'prop-types';
import { FormattedMessage, useIntl } from 'react-intl';
import { useAgent } from '../../AgentSystem/AgentProvider';

const DealTypeSelection = ({ formData, updateFormData, errors, company, currentUser }) => {
  const intl = useIntl();

  // Safely get agent context
  let spawnWidget;
  try {
    const agentContext = useAgent();
    spawnWidget = agentContext?.spawnWidget;
  } catch (e) {
    spawnWidget = null;
  }

  // Check if user has the copilot feature
  const hasCopilotFeature = company?.features?.includes('deal_teaser_content_creation_copilot') || currentUser?.features?.includes('deal_teaser_content_copilot');

  console.log('[DealTypeSelection] Debug:', {
    hasCopilotFeature,
    spawnWidget: !!spawnWidget,
    companyFeatures: company?.features,
    userFeatures: currentUser?.features
  });

  const dealTypes = [
    {
      id: 'succession',
      title: intl.formatMessage({ id: 'succession_deals.wizard.steps.deal_type_selection.types.succession.title' }),
      description: intl.formatMessage({ id: 'succession_deals.wizard.steps.deal_type_selection.types.succession.description' }),
      icon: (
        <svg className="h-8 w-8" fill="none" viewBox="0 0 24 24" stroke="currentColor">
          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M17 20h5v-2a3 3 0 00-5.356-1.857M17 20H7m10 0v-2c0-.656-.126-1.283-.356-1.857M7 20H2v-2a3 3 0 015.356-1.857M7 20v-2c0-.656.126-1.283.356-1.857m0 0a5.002 5.002 0 019.288 0M15 7a3 3 0 11-6 0 3 3 0 016 0zm6 3a2 2 0 11-4 0 2 2 0 014 0zM7 10a2 2 0 11-4 0 2 2 0 014 0z" />
        </svg>
      ),
      benefits: [
        intl.formatMessage({ id: 'succession_deals.wizard.steps.deal_type_selection.types.succession.benefits.transition_ownership' }),
        intl.formatMessage({ id: 'succession_deals.wizard.steps.deal_type_selection.types.succession.benefits.maintain_relationships' }),
        intl.formatMessage({ id: 'succession_deals.wizard.steps.deal_type_selection.types.succession.benefits.secure_legacy' }),
        intl.formatMessage({ id: 'succession_deals.wizard.steps.deal_type_selection.types.succession.benefits.structured_handover' })
      ]
    },
    {
      id: 'sale',
      title: intl.formatMessage({ id: 'succession_deals.wizard.steps.deal_type_selection.types.sale.title' }),
      description: intl.formatMessage({ id: 'succession_deals.wizard.steps.deal_type_selection.types.sale.description' }),
      icon: (
        <svg className="h-8 w-8" fill="none" viewBox="0 0 24 24" stroke="currentColor">
          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 8c-1.657 0-3 .895-3 2s1.343 2 3 2 3 .895 3 2-1.343 2-3 2m0-8c1.11 0 2.08.402 2.599 1M12 8V7m0 1v8m0 0v1m0-1c-1.11 0-2.08-.402-2.599-1M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
        </svg>
      ),
      benefits: [
        intl.formatMessage({ id: 'succession_deals.wizard.steps.deal_type_selection.types.sale.benefits.immediate_liquidity' }),
        intl.formatMessage({ id: 'succession_deals.wizard.steps.deal_type_selection.types.sale.benefits.market_pricing' }),
        intl.formatMessage({ id: 'succession_deals.wizard.steps.deal_type_selection.types.sale.benefits.clean_exit' }),
        intl.formatMessage({ id: 'succession_deals.wizard.steps.deal_type_selection.types.sale.benefits.full_value' })
      ]
    },
    {
      id: 'continuity',
      title: intl.formatMessage({ id: 'succession_deals.wizard.steps.deal_type_selection.types.continuity.title' }),
      description: intl.formatMessage({ id: 'succession_deals.wizard.steps.deal_type_selection.types.continuity.description' }),
      icon: (
        <svg className="h-8 w-8" fill="none" viewBox="0 0 24 24" stroke="currentColor">
          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12l2 2 4-4m5.618-4.016A11.955 11.955 0 0112 2.944a11.955 11.955 0 01-8.618 3.04A12.02 12.02 0 003 9c0 5.591 3.824 10.29 9 11.622 5.176-1.332 9-6.03 9-11.622 0-1.042-.133-2.052-.382-3.016z" />
        </svg>
      ),
      benefits: [
        intl.formatMessage({ id: 'succession_deals.wizard.steps.deal_type_selection.types.continuity.benefits.business_protection' }),
        intl.formatMessage({ id: 'succession_deals.wizard.steps.deal_type_selection.types.continuity.benefits.risk_mitigation' }),
        intl.formatMessage({ id: 'succession_deals.wizard.steps.deal_type_selection.types.continuity.benefits.operational_continuity' }),
        intl.formatMessage({ id: 'succession_deals.wizard.steps.deal_type_selection.types.continuity.benefits.client_service' })
      ]
    }
  ];

  const handleDealTypeChange = (dealType) => {
    updateFormData({ deal_type: dealType });
  };

  const handleAICopilot = () => {
    if (!hasCopilotFeature || !spawnWidget) return;

    spawnWidget(
      'content_creation_copilot',
      {
        dealId: formData.id,
        existingContent: {
          headline: formData.headline,
          summary: formData.summary_of_business,
          subHeadline: formData.sub_headline,
          idealCandidate: formData.ideal_candidate,
        },
        company: company, // Pass company with features for deal type selection
        onApprove: (approvedTeaser) => {
          // Map approved teaser to form fields
          if (approvedTeaser.deal_type) {
            updateFormData({ deal_type: approvedTeaser.deal_type });
          }
          if (approvedTeaser.headline) {
            updateFormData({ headline: approvedTeaser.headline });
          }
          if (approvedTeaser.overview) {
            updateFormData({ summary_of_business: approvedTeaser.overview });
          }
          if (approvedTeaser.key_metrics?.sub_headline || approvedTeaser.sub_headline) {
            updateFormData({ sub_headline: approvedTeaser.key_metrics?.sub_headline || approvedTeaser.sub_headline });
          }
          if (approvedTeaser.transition_terms) {
            updateFormData({ ideal_candidate: approvedTeaser.transition_terms });
          }

          alert('✅ AI-generated teaser has been applied! Review and edit as needed before publishing.');
        },
        onClose: () => {
          // Widget closed
        },
      },
      'modal'
    );
  };

  // Filter out continuity option if company has the hide feature
  const availableDealTypes = dealTypes.filter(type => {
    if (type.id === 'continuity') {
      // Check if company features include the hide continuity feature
      return !(company?.features && company.features.includes('hide_continuity_builder_create_in_deal_wizard'));
    }
    return true;
  });

  return (
    <div className="space-y-8">
      {/* Header */}
      <div className="text-center">
        <h2 className="text-2xl font-bold text-succession-text-primary">
          <FormattedMessage id="succession_deals.wizard.steps.deal_type_selection.title" />
        </h2>
        <p className="mt-2 text-lg text-succession-text-secondary">
          <FormattedMessage id="succession_deals.wizard.steps.deal_type_selection.description" />
        </p>
      </div>

      {/* Deal Type Cards */}
      <div className="grid gap-6 md:grid-cols-2 lg:grid-cols-3">
        {availableDealTypes.map((type) => (
          <div key={type.id} className="relative">
            <button
              type="button"
              onClick={() => handleDealTypeChange(type.id)}
              className={`w-full p-6 rounded-lg border-2 text-left transition-all duration-200 hover:shadow-succession-lg focus:outline-none focus:ring-2 focus:ring-succession-primary focus:ring-offset-2 ${
                formData.deal_type === type.id
                  ? 'border-succession-primary bg-succession-primary-10 shadow-succession'
                  : 'border-succession-border bg-succession-bg-primary hover:border-succession-primary'
              }`}
            >
              {/* Icon and Title */}
              <div className="flex items-center space-x-3 mb-4">
                <div className={`p-2 rounded-lg ${
                  formData.deal_type === type.id
                    ? 'bg-succession-primary text-white'
                    : 'bg-succession-bg-secondary text-succession-text-muted'
                }`}>
             
                  {type.icon}
                </div>
                <h3 className={`text-xl font-semibold ${
                  formData.deal_type === type.id ? 'text-succession-primary' : 'text-succession-text-primary'
                }`}>
                  {type.title}
                </h3>
              </div>

              {/* Description */}
              <p className={`text-sm mb-4 ${
                formData.deal_type === type.id ? 'text-succession-text-secondary' : 'text-succession-text-secondary'
              }`}>
                {type.description}
              </p>

              {/* Benefits */}
              <div className="space-y-2">
                <h4 className={`text-sm font-medium ${
                  formData.deal_type === type.id ? 'text-succession-primary' : 'text-succession-text-primary'
                }`}>
                  <FormattedMessage id="succession_deals.wizard.steps.deal_type_selection.key_benefits_label" />
                </h4>
                <ul className="space-y-1">
                  {type.benefits.map((benefit, index) => (
                    <li key={index} className={`flex items-center text-xs ${
                      formData.deal_type === type.id ? 'text-succession-primary' : 'text-succession-text-secondary'
                    }`}>
                      <svg className="h-3 w-3 mr-2 flex-shrink-0" fill="currentColor" viewBox="0 0 20 20">
                        <path fillRule="evenodd" d="M16.707 5.293a1 1 0 010 1.414l-8 8a1 1 0 01-1.414 0l-4-4a1 1 0 011.414-1.414L8 12.586l7.293-7.293a1 1 0 011.414 0z" clipRule="evenodd" />
                      </svg>
                      {benefit}
                    </li>
                  ))}
                </ul>
              </div>

              {/* Selected Indicator */}
              {formData.deal_type === type.id && (
                <div className="absolute top-4 right-4">
                  <div className="bg-succession-primary rounded-full p-1">
                    <svg className="h-4 w-4 text-white" fill="currentColor" viewBox="0 0 20 20">
                      <path fillRule="evenodd" d="M16.707 5.293a1 1 0 010 1.414l-8 8a1 1 0 01-1.414 0l-4-4a1 1 0 011.414-1.414L8 12.586l7.293-7.293a1 1 0 011.414 0z" clipRule="evenodd" />
                    </svg>
                  </div>
                </div>
              )}
            </button>
          </div>
        ))}
      </div>

      {/* Error Message */}
      {errors.deal_type && (
        <div className="rounded-md bg-red-50 p-4">
          <div className="flex">
            <div className="flex-shrink-0">
              <svg className="h-5 w-5 text-red-400" viewBox="0 0 20 20" fill="currentColor">
                <path fillRule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zM8.707 7.293a1 1 0 00-1.414 1.414L8.586 10l-1.293 1.293a1 1 0 101.414 1.414L10 11.414l1.293 1.293a1 1 0 001.414-1.414L11.414 10l1.293-1.293a1 1 0 00-1.414-1.414L10 8.586 8.707 7.293z" clipRule="evenodd" />
              </svg>
            </div>
            <div className="ml-3">
              <p className="text-sm text-red-800">{errors.deal_type}</p>
            </div>
          </div>
        </div>
      )}

      {/* Help Text */}
      <div className="rounded-md bg-blue-50 p-4">
        <div className="flex">
          <div className="flex-shrink-0">
            <svg className="h-5 w-5 text-blue-400" viewBox="0 0 20 20" fill="currentColor">
              <path fillRule="evenodd" d="M18 10a8 8 0 11-16 0 8 8 0 0116 0zm-7-4a1 1 0 11-2 0 1 1 0 012 0zM9 9a1 1 0 000 2v3a1 1 0 001 1h1a1 1 0 100-2v-3a1 1 0 00-1-1H9z" clipRule="evenodd" />
            </svg>
          </div>
          <div className="ml-3">
            <p className="text-sm text-blue-800">
              <FormattedMessage id="succession_deals.wizard.steps.deal_type_selection.help_text" />
            </p>
          </div>
        </div>
      </div>
    </div>
  );
};

DealTypeSelection.propTypes = {
  formData: PropTypes.object.isRequired,
  updateFormData: PropTypes.func.isRequired,
  errors: PropTypes.object,
  company: PropTypes.object,
  currentUser: PropTypes.object
};

DealTypeSelection.defaultProps = {
  errors: {},
  company: {},
  currentUser: null
};

export default DealTypeSelection; 
